import React, { useEffect, useRef, useState } from 'react';

/**
 * RunningDog — a fixed-position Golden Retriever sprite in the bottom-left
 * of the viewport. As the user scrolls, the dog gallops horizontally along
 * the bottom of the page. Uses requestAnimationFrame + transform-only
 * updates so motion stays smooth on every device.
 *
 * States (mode):
 *  - 'gallop': actively scrolling
 *  - 'idle':   stationary for < 20s (subtle bob)
 *  - 'sit':    stationary for ≥ 20s (dog "sits and pants" until the next
 *              scroll event kicks him back to gallop / idle)
 */
export const RunningDog = () => {
  const [mode, setMode] = useState('idle');  // 'gallop' | 'idle' | 'sit'
  const [direction, setDirection] = useState(1);
  const wrapRef = useRef(null);
  const lastScrollY = useRef(0);
  const currentX = useRef(0);   // rendered translateX in px
  const targetX = useRef(0);    // where the dog wants to be, in px
  const rafRef = useRef(null);
  const galloptoIdleTimer = useRef(null);
  const idleToSitTimer = useRef(null);

  useEffect(() => {
    const measureTarget = () => {
      const y = window.scrollY || 0;
      const max = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
      const progress = Math.max(0, Math.min(1, y / max));
      // Sprite width in CSS px — 130 on mobile (< 640), 260 on ≥ 640.
      const isMobile = window.innerWidth < 640;
      const spriteW = isMobile ? 130 : 260;
      // Let the dog use the full viewport width on every screen. On mobile
      // the Message-Greg bubble sits at z-index 70 while the running dog is
      // at z-index 40, so if the sprite briefly passes under Greg near the
      // right edge the bubble simply layers over it — no visual collision.
      const leftStart = window.innerWidth * 0.02;
      const rightStop = Math.max(leftStart, window.innerWidth - spriteW - (window.innerWidth * 0.02));
      targetX.current = leftStart + progress * (rightStop - leftStart);
    };

    const tick = () => {
      const dx = targetX.current - currentX.current;
      currentX.current += dx * 0.15;
      if (Math.abs(dx) < 0.4) currentX.current = targetX.current;
      const el = wrapRef.current;
      if (el) el.style.transform = `translateX(${currentX.current}px)`;
      rafRef.current = requestAnimationFrame(tick);
    };

    const armIdleToSit = () => {
      // After 10s of no scroll, dog decides to sit down and pant.
      if (idleToSitTimer.current) window.clearTimeout(idleToSitTimer.current);
      idleToSitTimer.current = window.setTimeout(() => setMode('sit'), 10000);
    };

    const onScroll = () => {
      const y = window.scrollY || 0;
      const delta = y - lastScrollY.current;
      if (Math.abs(delta) > 1) {
        setDirection(delta > 0 ? 1 : -1);
        setMode('gallop');
        // He stands back up + starts trotting → resets both timers
        if (galloptoIdleTimer.current) window.clearTimeout(galloptoIdleTimer.current);
        galloptoIdleTimer.current = window.setTimeout(() => {
          setMode('idle');
          armIdleToSit();
        }, 380);
        if (idleToSitTimer.current) window.clearTimeout(idleToSitTimer.current);
      }
      lastScrollY.current = y;
      measureTarget();
    };

    measureTarget();
    currentX.current = targetX.current;
    if (wrapRef.current) {
      wrapRef.current.style.transform = `translateX(${currentX.current}px)`;
    }

    rafRef.current = requestAnimationFrame(tick);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', measureTarget);

    // Kick off the initial idle-to-sit countdown on mount
    armIdleToSit();

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', measureTarget);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (galloptoIdleTimer.current) window.clearTimeout(galloptoIdleTimer.current);
      if (idleToSitTimer.current) window.clearTimeout(idleToSitTimer.current);
    };
  }, []);

  const stateClass = mode === 'gallop'
    ? 'running-dog-img--gallop'
    : mode === 'sit'
      ? 'running-dog-img--sit'
      : 'running-dog-img--idle';

  return (
    <div
      ref={wrapRef}
      className='running-dog-wrap'
      style={{ '--rd-dir': direction }}
      aria-hidden='true'
      data-testid='running-dog'
      data-mode={mode}
    >
      <img
        src='/img/running-dog.png'
        alt=''
        className={`running-dog-img ${stateClass}`}
        draggable={false}
      />
    </div>
  );
};

export default RunningDog;
